<script setup lang="ts">
  import { Input, Button, Checkbox, Typo } from '@travelata-libs/ui-kit';
  import { ref, computed, watch } from 'vue';
  import type { LeadBannerPayload } from './LeadBanner.types';
  import { PHONE_REGEX, EMPTY_NAME_MESSAGE, EMPTY_PHONE_MESSAGE } from './lib';

  interface LeadBannerFormProps {
    modelValue: LeadBannerPayload;
    loading: boolean;
    mobile: boolean;
    userAgreementUrl: string;
    privacyPolicyUrl: string;
    dataProcessingUrl: string;
    buttonText: string;
  }

  const props = defineProps<LeadBannerFormProps>();

  const emit = defineEmits<{
    (e: 'submit', payload: LeadBannerPayload): void;
    (e: 'update:modelValue', value: LeadBannerPayload): void;
    (e: 'focus'): void;
  }>();

  const emitModelValue = (data: LeadBannerPayload) => {
    emit('update:modelValue', data);
  };

  const name = computed({
    get: () => props.modelValue.name,
    set: (value: string) => {
      emitModelValue({ ...props.modelValue, name: value });
    },
  });

  const phone = computed({
    get: () => props.modelValue.phone,
    set: (value: string) => {
      emitModelValue({ ...props.modelValue, phone: value });
    },
  });
  const agreementChecked = ref(true);
  const dataProcessingChecked = ref(true);
  const nameError = ref('');
  const phoneError = ref('');
  const agreementError = ref(false);
  const dataProcessingError = ref(false);

  const isFormValid = computed(
    () =>
      name.value.trim().length > 0 &&
      PHONE_REGEX.test(phone.value) &&
      agreementChecked.value &&
      dataProcessingChecked.value,
  );

  const onSubmit = () => {
    nameError.value = name.value.trim() ? '' : EMPTY_NAME_MESSAGE;
    phoneError.value = PHONE_REGEX.test(phone.value) ? '' : EMPTY_PHONE_MESSAGE;
    agreementError.value = !agreementChecked.value;
    dataProcessingError.value = !dataProcessingChecked.value;

    if (nameError.value || phoneError.value) return;
    if (!isFormValid.value) return;

    emit('submit', { name: name.value.trim(), phone: phone.value });
  };

  watch(name, () => {
    if (nameError.value) nameError.value = '';
  });

  watch(phone, () => {
    if (phoneError.value) phoneError.value = '';
  });

  watch(agreementChecked, () => {
    if (agreementError.value) agreementError.value = false;
  });

  watch(dataProcessingChecked, () => {
    if (dataProcessingError.value) dataProcessingError.value = false;
  });

  const nameInputRef = ref<{ focus: () => void; blur: () => void } | null>(null);
  const phoneInputRef = ref<{ focus: () => void; blur: () => void } | null>(null);

  defineExpose({
    focusName: () => nameInputRef.value?.focus(),
    blur: () => {
      nameInputRef.value?.blur();
      phoneInputRef.value?.blur();
    },
  });
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div>
    <div
      :class="[
        'tl-lead-banner__fields',
        {
          'tl-lead-banner__fields_mobile': mobile,
        },
      ]"
    >
      <Input
        ref="nameInputRef"
        v-model="name"
        size="l"
        label="Имя"
        :error="nameError"
        data-test-id="lead-banner-name"
        @focus="$emit('focus')"
      />
      <Input
        ref="phoneInputRef"
        v-model="phone"
        type="tel"
        size="l"
        label="Телефон"
        :unmasked="true"
        :error="phoneError"
        data-test-id="lead-banner-phone"
        @focus="$emit('focus')"
      />
    </div>

    <div
      :class="[
        'tl-lead-banner__checkboxes',
        {
          'tl-lead-banner__checkboxes_mobile': mobile,
        },
      ]"
    >
      <Checkbox v-model="agreementChecked" size="s" :error="agreementError" data-test-id="lead-banner-agreement">
        <Typo variant="body-s-400" tag="span">
          Принимаю
          <a :href="userAgreementUrl" target="_blank" rel="noopener noreferrer" class="tl-lead-banner__checkbox-link">
            Пользовательское соглашение
          </a>
          <br />
          и
          <a :href="privacyPolicyUrl" target="_blank" rel="noopener noreferrer" class="tl-lead-banner__checkbox-link">
            Политику о персональных данных
          </a>
        </Typo>
      </Checkbox>
      <Checkbox
        v-model="dataProcessingChecked"
        size="s"
        :error="dataProcessingError"
        data-test-id="lead-banner-data-processing"
      >
        <Typo variant="body-s-400" tag="span">
          Даю
          <a :href="dataProcessingUrl" target="_blank" rel="noopener noreferrer" class="tl-lead-banner__checkbox-link">
            Согласие на обработку персональных данных
          </a>
        </Typo>
      </Checkbox>
    </div>

    <Button :loading="loading" data-test-id="lead-banner-submit" @click="onSubmit">{{ buttonText }}</Button>
  </div>
</template>

<style lang="scss" scoped>
  .tl-lead-banner {
    &__fields {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-s);
      margin-bottom: var(--spacing-xl);

      &_mobile {
        gap: var(--spacing-m);
      }
    }

    &__checkboxes {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xxs);
      margin-bottom: var(--spacing-s);

      &_mobile {
        gap: var(--spacing-xs);
        margin-bottom: var(--spacing-m);
      }
    }

    &__checkbox-link {
      color: var(--color-brand-secondary);
      text-decoration: none;
      white-space: normal;

      &:hover {
        text-decoration: underline;
      }
    }
  }
</style>
